import React from 'react';
import { Button } from 'antd';

interface OfferDisplayProps {
	offerData: {
		interval: string;
		intervalCount: number;
		productName: string;
		trialDays: number;
		type: string;
		quantity: number;
		price: string;
		features?: any;
	};
	onStartClick: () => void;
	isLoading?: boolean;
	customerName?: string;
	businessName?: string;
}

// Feature display component
const FeatureDisplay: React.FC<{ features: any }> = ({ features }) => {
	const getFeatureValue = (feature: any) => {
		// Handle team access special case
		if (feature.viewAccessMembers && feature.editAccessMembers) {
			// This is a team access feature with nested properties
			return {
				viewAccessMembers: getFeatureValue(feature.viewAccessMembers),
				editAccessMembers: getFeatureValue(feature.editAccessMembers),
			};
		}

		// Handle voice provider pricing special case
		if (feature.charges) {
			return feature.charges;
		}

		if (!feature.gatedValues || feature.gatedValues.length === 0) {
			return feature.value;
		}

		// Find the most recent applicable value
		const now = new Date();
		let applicableValue = null;

		for (const gatedValue of feature.gatedValues) {
			if (gatedValue.effectiveAfter === 0) {
				// If effectiveAfter is 0, it's always applicable
				applicableValue = gatedValue.value;
				break;
			} else {
				const effectiveDate = new Date(gatedValue.effectiveAfter);
				if (effectiveDate <= now) {
					applicableValue = gatedValue.value;
				}
			}
		}

		return applicableValue !== null ? applicableValue : feature.value;
	};

	const formatFeatureValue = (value: any, feature: any) => {
		if (typeof value === 'boolean') {
			return value ? '✓ Enabled' : '✗ Disabled';
		}
		if (value === -1) {
			return 'Unlimited';
		}
		if (typeof value === 'number') {
			if (feature.displayText?.toLowerCase().includes('minutes')) {
				return `${value} credits`;
			}
			if (feature.displayText?.toLowerCase().includes('credits')) {
				return `${value} credits`;
			}
			if (feature.displayText?.toLowerCase().includes('hours')) {
				return `${value} hours`;
			}
			if (feature.displayText?.toLowerCase().includes('pages')) {
				return `${value} pages`;
			}
			// Handle team access members
			if (
				feature.displayText?.toLowerCase().includes('team access') ||
				feature.displayText?.toLowerCase().includes('members')
			) {
				return `${value} members`;
			}
			return value.toString();
		}
		return value?.toString() || 'N/A';
	};

	const getFeatureIcon = (category: string, featureName: string) => {
		const iconMap: { [key: string]: string } = {
			// Communication & Call Handling
			minutesIncluded: '📞',
			overageVoiceCharges: '💰',
			premiumAiModels: '🤖',
			premiumVoices: '🎤',
			smsCreditsIncluded: '💬',
			stateOfArtLatency: '⚡',
			unlimitedParallelCalls: '🔄',

			// Analytics & Reporting
			analyticsTab: '📊',
			logs: '📋',
			maxNotificationRecipients: '🔔',
			postCallNotifications: '📱',
			workflowNotifications: '⚙️',

			// Advanced Capabilities
			multilingualSupport: '🌍',
			setMaxRecipientMinutes: '⏱️',
			teamAccess: '👥',
			timeControls: '🕐',
			zapierIntegration: '🔗',

			// Knowledge & CRM
			advancedCRM: '📈',
			knowledgeBaseLengthPages: '📚',

			// Phone Numbers & Forwarding
			keepYourPhoneNumber: '📱',
			pickYourAreaCode: '📍',
			recieveAStandaloneNumber: '📞',

			// Support & SLA
			supportCalls: '🎧',
			supportSLAWithInHours: '⏰',

			// Webhooks
			customWebhooks: '🔌',

			// Workflows
			apiWorkflows: '🔧',
			callTransferWorkflows: '🔄',
			intakeFormWorkflows: '📝',
			overThePhoneBookWorkflows: '📅',
			postCallMessage: '💬',
			textWorkflows: '📱',
		};

		return iconMap[featureName] || '✨';
	};

	const getFeatureColor = (value: any) => {
		if (typeof value === 'boolean') {
			return value ? 'from-green-500 to-emerald-500' : 'from-gray-400 to-gray-500';
		}
		if (value === -1 || (typeof value === 'number' && value > 0)) {
			return 'from-blue-500 to-purple-500';
		}
		return 'from-orange-500 to-red-500';
	};

	const renderFeature = (featureName: string, feature: any) => {
		const value = getFeatureValue(feature);
		const icon = getFeatureIcon('', featureName);

		// Debug logging for team access and voice provider pricing
		if (featureName === 'teamAccess' || featureName === 'overageVoiceCharges') {
			console.log(`Feature: ${featureName}`, { feature, value });
		}

		// Handle team access special case
		if (
			featureName === 'teamAccess' &&
			typeof value === 'object' &&
			value.viewAccessMembers !== undefined
		) {
			return (
				<div
					key={featureName}
					className="p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-all duration-300 group"
				>
					<div className="flex items-center mb-3">
						<div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg flex items-center justify-center mr-4 group-hover:scale-110 transition-transform duration-300 text-white text-lg">
							{icon}
						</div>
						<div className="flex-1">
							<div className="font-semibold text-gray-900">{feature.displayText}</div>
							<div className="text-gray-600 text-sm">{feature.tooltip}</div>
						</div>
					</div>
					<div className="ml-14 space-y-2">
						<div className="flex items-center justify-between">
							<span className="text-sm font-medium text-gray-700">View Access Members:</span>
							<span className="px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
								{value.viewAccessMembers === -1
									? 'Unlimited'
									: `${value.viewAccessMembers} members`}
							</span>
						</div>
						<div className="flex items-center justify-between">
							<span className="text-sm font-medium text-gray-700">Edit Access Members:</span>
							<span className="px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
								{value.editAccessMembers === -1
									? 'Unlimited'
									: `${value.editAccessMembers} members`}
							</span>
						</div>
					</div>
				</div>
			);
		}

		// Handle voice provider pricing special case
		if (
			featureName === 'overageVoiceCharges' &&
			typeof value === 'object' &&
			value.openai !== undefined
		) {
			return (
				<div
					key={featureName}
					className="p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-all duration-300 group"
				>
					<div className="flex items-center mb-3">
						<div className="w-10 h-10 bg-gradient-to-r from-green-500 to-emerald-500 rounded-lg flex items-center justify-center mr-4 group-hover:scale-110 transition-transform duration-300 text-white text-lg">
							{icon}
						</div>
						<div className="flex-1">
							<div className="font-semibold text-gray-900">{feature.displayText}</div>
							<div className="text-gray-600 text-sm">{feature.tooltip}</div>
						</div>
					</div>
					<div className="ml-14">
						<div className="grid grid-cols-2 md:grid-cols-3 gap-2">
							{Object.entries(value).map(([provider, price]) => (
								<div
									key={provider}
									className="flex items-center justify-between p-2 bg-white rounded border"
								>
									<span className="text-sm font-medium text-gray-700 capitalize">
										{provider === 'transferredCall' ? 'Transfer Call' : provider}
									</span>
									<span className="text-sm font-bold text-green-600">${price as number}/min</span>
								</div>
							))}
						</div>
					</div>
				</div>
			);
		}

		const formattedValue = formatFeatureValue(value, feature);
		const colorClass = getFeatureColor(value);

		// Skip disabled features
		if (typeof value === 'boolean' && !value) {
			return null;
		}

		return (
			<div
				key={featureName}
				className="flex items-center p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-all duration-300 group"
			>
				<div
					className={`w-10 h-10 bg-gradient-to-r ${colorClass} rounded-lg flex items-center justify-center mr-4 group-hover:scale-110 transition-transform duration-300 text-white text-lg`}
				>
					{icon}
				</div>
				<div className="flex-1">
					<div className="font-semibold text-gray-900">{feature.displayText}</div>
					<div className="text-gray-600 text-sm">{feature.tooltip}</div>
				</div>
				<div className="ml-4">
					<span
						className={`px-3 py-1 rounded-full text-sm font-medium ${
							typeof value === 'boolean' && value
								? 'bg-green-100 text-green-800'
								: value === -1
									? 'bg-blue-100 text-blue-800'
									: typeof value === 'number' && value > 0
										? 'bg-purple-100 text-purple-800'
										: 'bg-gray-100 text-gray-800'
						}`}
					>
						{formattedValue}
					</span>
				</div>
			</div>
		);
	};

	const renderCategory = (categoryName: string, category: any) => {
		const categoryIcons: { [key: string]: { icon: string; title: string; description: string } } = {
			communicationAndCallHandling: {
				icon: '📞',
				title: 'Communication & Call Handling',
				description: 'Core calling and messaging features',
			},
			AnalyticsAndReporting: {
				icon: '📊',
				title: 'Analytics & Reporting',
				description: 'Insights and data tracking',
			},
			advancedCapabilities: {
				icon: '🚀',
				title: 'Advanced Capabilities',
				description: 'Premium features and integrations',
			},
			knowledgeAndCRM: {
				icon: '📚',
				title: 'Knowledge & CRM',
				description: 'Business intelligence and customer management',
			},
			phoneNumbersAndForwarding: {
				icon: '📱',
				title: 'Phone Numbers & Forwarding',
				description: 'Phone number management',
			},
			supportAndSLA: {
				icon: '🎧',
				title: 'Support & SLA',
				description: 'Customer support and service level agreements',
			},
			webhooks: {
				icon: '🔌',
				title: 'Webhooks',
				description: 'External integrations and APIs',
			},
			workflows: {
				icon: '⚙️',
				title: 'Workflows',
				description: 'Automated business processes',
			},
		};

		const categoryInfo = categoryIcons[categoryName] || {
			icon: '✨',
			title: categoryName,
			description: '',
		};

		const features = Object.entries(category)
			.map(([featureName, feature]) => renderFeature(featureName, feature as any))
			.filter(Boolean);

		if (features.length === 0) {
			return null;
		}

		return (
			<div key={categoryName} className="mb-8">
				<div className="flex items-center mb-4">
					<div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg flex items-center justify-center mr-3 text-white text-sm">
						{categoryInfo.icon}
					</div>
					<div>
						<h4 className="text-lg font-bold text-gray-900">{categoryInfo.title}</h4>
						{categoryInfo.description && (
							<p className="text-sm text-gray-600">{categoryInfo.description}</p>
						)}
					</div>
				</div>
				<div className="space-y-3">{features}</div>
			</div>
		);
	};

	if (!features) {
		return null;
	}

	return (
		<div className="mb-10">
			<h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
				<svg className="w-6 h-6 text-blue-600 mr-3" fill="currentColor" viewBox="0 0 20 20">
					<path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
				</svg>
				What's included in your plan:
			</h3>
			<div>
				{Object.entries(features).map(([categoryName, category]) =>
					renderCategory(categoryName, category),
				)}
			</div>
		</div>
	);
};

const OfferDisplay: React.FC<OfferDisplayProps> = ({
	offerData,
	onStartClick,
	isLoading = false,
	customerName,
	businessName,
}) => {
	const formatInterval = (interval: string, count: number) => {
		if (count === 1) {
			return interval;
		}
		return `${count} ${interval}${count > 1 ? 's' : ''}`;
	};

	const formatTrialPeriod = (days: number) => {
		if (days === 0) {
			return 'No trial';
		}
		if (days === 1) {
			return '1 day trial';
		}
		return `${days} days trial`;
	};

	return (
		<div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center px-4 py-8 relative overflow-hidden">
			{/* Background decorative elements */}
			<div className="absolute inset-0 overflow-hidden pointer-events-none">
				<div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-blue-400/20 to-purple-400/20 rounded-full blur-3xl" />
				<div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-indigo-400/20 to-blue-400/20 rounded-full blur-3xl" />
				<div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-r from-purple-400/10 to-blue-400/10 rounded-full blur-3xl" />
			</div>

			<div className="max-w-4xl w-full relative z-10">
				{/* Header with special offer badge */}
				<div className="text-center mb-12 animate-in slide-in-from-top-4 duration-1000">
					<div className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-purple-600 via-blue-600 to-indigo-600 text-white text-sm font-semibold rounded-full mb-6 shadow-xl backdrop-blur-sm border border-white/20 animate-pulse hover:animate-none hover:scale-105 transition-all duration-300">
						<svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
							<path
								fillRule="evenodd"
								d="M5 2a1 1 0 011 1v1h1a1 1 0 010 2H6v1a1 1 0 01-2 0V6H3a1 1 0 010-2h1V3a1 1 0 011-1zm0 10a1 1 0 011 1v1h1a1 1 0 110 2H6v1a1 1 0 11-2 0v-1H3a1 1 0 110-2h1v-1a1 1 0 011-1zM12 2a1 1 0 01.967.744L14.146 7.2 17.5 9.134a1 1 0 010 1.732L14.146 12.8 12.967 17.256a1 1 0 01-1.934 0L9.854 12.8 6.5 10.866a1 1 0 010-1.732L9.854 7.2 11.033 2.744A1 1 0 0112 2z"
								clipRule="evenodd"
							/>
						</svg>
						Special Offer {customerName && `for ${customerName}`}
					</div>
					<h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 bg-clip-text text-transparent leading-tight">
						Exclusive Deal
						{businessName && (
							<span className="block text-4xl md:text-5xl mt-2">for {businessName}</span>
						)}
					</h1>
					<p className="text-xl text-gray-600 max-w-lg mx-auto leading-relaxed">
						Transform your business with our AI-powered solution designed for modern teams
					</p>
				</div>

				{/* Main offer card */}
				<div className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-2xl overflow-hidden border border-white/20 animate-in slide-in-from-bottom-4 duration-1000 delay-300 hover:shadow-3xl transition-all duration-500 group">
					{/* Gradient header */}
					<div className="bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-700 px-8 py-8 text-white relative overflow-hidden">
						{/* Animated background pattern */}
						<div className="absolute inset-0 opacity-10">
							<div className="absolute top-0 left-0 w-40 h-40 bg-white rounded-full -translate-x-20 -translate-y-20 animate-pulse" />
							<div className="absolute bottom-0 right-0 w-32 h-32 bg-white rounded-full translate-x-16 translate-y-16 animate-pulse delay-1000" />
							<div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-24 h-24 bg-white rounded-full animate-pulse delay-500" />
						</div>
						<div className="relative flex items-center justify-between">
							<div>
								<h2 className="text-3xl font-bold mb-3">{offerData.productName}</h2>
								<p className="text-blue-100 text-lg font-medium">
									{formatInterval(offerData.interval, offerData.intervalCount)} subscription
								</p>
							</div>
							<div className="text-right">
								<div className="text-4xl font-bold mb-1">
									{offerData?.type === 'unlimited' ? '∞' : offerData.quantity + 'x'}
								</div>
								<div className="text-blue-100 font-medium">AI Agents</div>
							</div>
						</div>

						{/* Price display */}
						<div className="mt-6 pt-6 border-t border-white/20">
							<div className="flex items-center justify-center">
								<div className="text-center animate-in slide-in-from-bottom-2 duration-700 delay-500">
									<div className="text-5xl font-bold bg-gradient-to-r from-white to-blue-100 bg-clip-text text-transparent mb-2">
										${offerData.price}
									</div>
									<div className="text-sm text-blue-200 font-medium">
										per {formatInterval(offerData.interval, offerData.intervalCount)}
									</div>
								</div>
							</div>
						</div>
					</div>

					{/* Offer details */}
					<div className="p-8 bg-white/90 backdrop-blur-sm">
						{/* Trial period */}
						{offerData.trialDays > 0 && (
							<div className="mb-8">
								<div className="flex items-center justify-between p-6 bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl border border-green-200 hover:bg-green-100 transition-all duration-300 group">
									<div className="flex items-center">
										<div className="w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-500 rounded-xl flex items-center justify-center mr-4 group-hover:scale-110 transition-transform duration-300">
											<svg
												className="w-6 h-6 text-white"
												fill="none"
												stroke="currentColor"
												viewBox="0 0 24 24"
											>
												<path
													strokeLinecap="round"
													strokeLinejoin="round"
													strokeWidth={2}
													d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
												/>
											</svg>
										</div>
										<div>
											<div className="font-bold text-green-800 text-lg">Free Trial</div>
											<div className="text-green-600 font-medium">
												{formatTrialPeriod(offerData.trialDays)}
											</div>
										</div>
									</div>
									<div className="text-green-500 font-bold text-2xl">
										{offerData.trialDays > 0 ? '✓' : '—'}
									</div>
								</div>
							</div>
						)}

						{/* Features list */}
						{offerData.features ? (
							<FeatureDisplay features={offerData.features} />
						) : (
							<div className="mb-10">
								<h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
									<svg
										className="w-6 h-6 text-blue-600 mr-3"
										fill="currentColor"
										viewBox="0 0 20 20"
									>
										<path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
									</svg>
									What's included in your plan:
								</h3>
								<div className="space-y-4">
									<div className="flex items-center p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-all duration-300 group">
										<div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg flex items-center justify-center mr-4 group-hover:scale-110 transition-transform duration-300">
											<svg
												className="w-5 h-5 text-white"
												fill="none"
												stroke="currentColor"
												viewBox="0 0 24 24"
											>
												<path
													strokeLinecap="round"
													strokeLinejoin="round"
													strokeWidth={2}
													d="M13 10V3L4 14h7v7l9-11h-7z"
												/>
											</svg>
										</div>
										<div>
											<div className="font-semibold text-gray-900">
												{offerData?.type === 'unlimited'
													? 'Unlimited AI Agents'
													: offerData.quantity + ' AI Agents per ' + offerData.interval}
											</div>
											<div className="text-gray-600 text-sm">
												Advanced AI-powered customer service
											</div>
										</div>
									</div>
									<div className="flex items-center p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-all duration-300 group">
										<div className="w-10 h-10 bg-gradient-to-r from-green-500 to-teal-500 rounded-lg flex items-center justify-center mr-4 group-hover:scale-110 transition-transform duration-300">
											<svg
												className="w-5 h-5 text-white"
												fill="none"
												stroke="currentColor"
												viewBox="0 0 24 24"
											>
												<path
													strokeLinecap="round"
													strokeLinejoin="round"
													strokeWidth={2}
													d="M5 13l4 4L19 7"
												/>
											</svg>
										</div>
										<div>
											<div className="font-semibold text-gray-900">Full Feature Access</div>
											<div className="text-gray-600 text-sm">
												Unlock all premium features and integrations
											</div>
										</div>
									</div>
									<div className="flex items-center p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-all duration-300 group">
										<div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center mr-4 group-hover:scale-110 transition-transform duration-300">
											<svg
												className="w-5 h-5 text-white"
												fill="none"
												stroke="currentColor"
												viewBox="0 0 24 24"
											>
												<path
													strokeLinecap="round"
													strokeLinejoin="round"
													strokeWidth={2}
													d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192L5.636 18.364M12 2.25a9.75 9.75 0 100 19.5 9.75 9.75 0 000-19.5z"
												/>
											</svg>
										</div>
										<div>
											<div className="font-semibold text-gray-900">Priority Support</div>
											<div className="text-gray-600 text-sm">
												Dedicated support team with 24/7 availability
											</div>
										</div>
									</div>
								</div>
							</div>
						)}

						{/* Action button */}
						<div className="text-center">
							<Button
								type="primary"
								size="large"
								onClick={onStartClick}
								loading={isLoading}
								className="h-16 px-10 text-xl font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 border-0 rounded-2xl shadow-2xl hover:shadow-3xl transform hover:scale-105 transition-all duration-300 relative overflow-hidden group"
							>
								<span className="relative z-10 flex items-center">
									<svg
										className="w-6 h-6 mr-3"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M13 10V3L4 14h7v7l9-11h-7z"
										/>
									</svg>
									{isLoading ? 'Processing...' : 'Get Started'}
								</span>
								<div className="absolute inset-0 bg-gradient-to-r from-indigo-600 via-purple-600 to-blue-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
							</Button>
							<div className="mt-6 flex items-center justify-center space-x-6 text-sm text-gray-500">
								<div className="flex items-center">
									<svg
										className="w-4 h-4 mr-2 text-green-500"
										fill="currentColor"
										viewBox="0 0 20 20"
									>
										<path
											fillRule="evenodd"
											d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
											clipRule="evenodd"
										/>
									</svg>
									<span>Secure checkout</span>
								</div>
							</div>
						</div>
					</div>
				</div>

				{/* Trust indicators */}
				<div className="mt-12 text-center animate-in fade-in duration-1000 delay-700">
					<div className="bg-white/60 backdrop-blur-sm rounded-2xl p-6 border border-white/20 shadow-xl">
						<div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-8 text-gray-600">
							<div className="flex items-center justify-center sm:justify-start">
								<div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
									<svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
										<path
											fillRule="evenodd"
											d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
											clipRule="evenodd"
										/>
									</svg>
								</div>
								<span className="font-medium text-center sm:text-left">Secure Platform</span>
							</div>
							<div className="flex items-center justify-center sm:justify-start">
								<div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
									<svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
										<path
											fillRule="evenodd"
											d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
											clipRule="evenodd"
										/>
									</svg>
								</div>
								<span className="font-medium text-center sm:text-left">24/7 Support</span>
							</div>
							<div className="flex items-center justify-center sm:justify-start">
								<div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
									<svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
										<path
											fillRule="evenodd"
											d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
											clipRule="evenodd"
										/>
									</svg>
								</div>
								<span className="font-medium text-center sm:text-left">99.9% Uptime</span>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default OfferDisplay;
